// Frontend observability — Phase 0a multi-user readiness.
//
// Initializes Sentry for the React app. No-ops when VITE_SENTRY_DSN is
// unset so local `npm run dev` without a Sentry project still works.
//
// Two job:
//   1. Catch unhandled exceptions, React render errors, and unhandled
//      promise rejections; send them to Sentry with full stack traces
//      (source maps uploaded at build time via @sentry/vite-plugin).
//   2. Carry the X-Request-Id we get from backend responses onto the
//      Sentry scope so subsequent errors correlate to the exact
//      backend request that preceded them.
//
// The scope plumbing lives in `lib/api.ts` (axios response interceptor)
// — this file only owns init.

import * as Sentry from "@sentry/react";

const SENSITIVE_KEYS = new Set([
  "body",
  "description",
  "notes",
  "content",
  "comment",
  "markdown",
]);

function scrub(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(scrub);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = SENSITIVE_KEYS.has(k) ? "[redacted]" : scrub(v);
    }
    return out;
  }
  return value;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
      "production",
    // BrowserTracing samples actual page navigations + API calls.
    // 1.0 is fine at our scale; tune down if the free tier fills up.
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    // Don't send default PII (cookies, IPs). Add user identity
    // explicitly once auth lands.
    sendDefaultPii: false,
    beforeSend(event) {
      // Walk request data + breadcrumbs and redact our sensitive keys.
      // Sentry's built-in scrubbers don't know our schema.
      if (event.request?.data) {
        event.request.data = scrub(event.request.data) as typeof event.request.data;
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((bc) => ({
          ...bc,
          data: bc.data ? (scrub(bc.data) as typeof bc.data) : bc.data,
        }));
      }
      return event;
    },
  });
}

// Re-export the ErrorBoundary so consumers don't import @sentry/react
// directly. Lets us swap providers later without churn.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
